import React, { useState, useEffect } from 'react';
import PlantPerformancePanel from './PlantPerformancePanel';
import AlertDashboard from './AlertDashboard';
import CommunicationStatusCard from './CommunicationStatusCard';
import { 
  faultIntelligenceService, 
  PlantPerformance, 
  AlertsResponse, 
  CommunicationResponse 
} from '../../services/faultIntelligenceService';

interface GroundMountedDashboardProps {
  plantId: string;
  selectedDate?: string;
}

const GroundMountedDashboard: React.FC<GroundMountedDashboardProps> = ({
  plantId,
  selectedDate = new Date().toISOString().split('T')[0]
}) => {
  const [performance, setPerformance] = useState<PlantPerformance | null>(null);
  const [alertsData, setAlertsData] = useState<AlertsResponse | null>(null);
  const [communicationData, setCommunicationData] = useState<CommunicationResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [alertFilters, setAlertFilters] = useState({ type: '', status: 'Active' });

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      const [performanceData, alertsResponse, commData] = await Promise.all([
        faultIntelligenceService.getPerformance(plantId, selectedDate),
        faultIntelligenceService.getAlerts({
          plantId,
          date: selectedDate,
          ...alertFilters
        }),
        faultIntelligenceService.getCommunicationStatus(plantId)
      ]);

      setPerformance(performanceData);
      setAlertsData(alertsResponse);
      setCommunicationData(commData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      // Mock data fallback
      setPerformance({
        id: 1, plant: parseInt(plantId), plant_name: plantId === '1' ? 'PRSP_HRJNGL' : 'Aviyur 10MW',
        date: selectedDate, pr: plantId === '1' ? 78.5 : 0, cuf: plantId === '1' ? 22.3 : 0,
        irradiation: plantId === '1' ? 850 : 0, energy_kwh: plantId === '1' ? 125000 : 0,
        active_power_kw: plantId === '1' ? 42500 : 0, grid_availability: plantId === '1' ? 98.5 : 0,
        plant_availability: plantId === '1' ? 96.2 : 0, co2_saved: plantId === '1' ? 62.5 : 0, trees_saved: plantId === '1' ? 156 : 0
      });
      setAlertsData({ alerts: [{ alert_id: '1', plant: parseInt(plantId), plant_name: plantId === '1' ? 'PRSP_HRJNGL' : 'Aviyur 10MW',
        equipment_id: 'INV-001', type: 'Communication', status: 'Active', severity: 'High',
        title: 'Communication Failure', message: 'Server unavailable - showing mock data', first_seen: new Date().toISOString(),
        last_seen: new Date().toISOString(), count: 1, duration: 15 }], counts: { Communication: 1 }, total: 1 });
      setCommunicationData({ devices: [{ id: 1, plant: parseInt(plantId), plant_name: plantId === '1' ? 'PRSP_HRJNGL' : 'Aviyur 10MW',
        equipment_id: 'INV-001', equipment_type: 'inverter', status: 'Offline', heartbeat_interval: 300,
        failure_count: 1, is_online: false, last_seen_minutes: 15 }], failure_count: 1, total_devices: 1 });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (plantId) {
      loadDashboardData();
    }
  }, [plantId, selectedDate]);

  const handleAlertFilterChange = async (filters: { type?: string; status?: string }) => {
    setAlertFilters(prev => ({ ...prev, ...filters }));
    
    try {
      const alertsResponse = await faultIntelligenceService.getAlerts({
        plantId,
        date: selectedDate,
        type: filters.type || alertFilters.type || undefined,
        status: filters.status || alertFilters.status || undefined
      });
      setAlertsData(alertsResponse);
    } catch (error) {
      console.error('Failed to filter alerts:', error);
    }
  };

  const handleAcknowledgeAlert = async (alertId: string) => {
    try {
      await faultIntelligenceService.acknowledgeAlert(alertId, 'System User');
      // Reload alerts to reflect changes
      handleAlertFilterChange(alertFilters);
    } catch (error) {
      console.error('Failed to acknowledge alert:', error);
    }
  };

  const handleResolveAlert = async (alertId: string) => {
    try {
      await faultIntelligenceService.resolveAlert(alertId);
      // Reload alerts to reflect changes
      handleAlertFilterChange(alertFilters);
    } catch (error) {
      console.error('Failed to resolve alert:', error);
    }
  };

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
            Ground Mounted Dashboard
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Fault Intelligence & Operational Alerting
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-500">
            Plant: <span className="font-medium">{performance?.plant_name || 'Loading...'}</span>
          </div>
          <div className="text-sm text-gray-500">
            Date: <span className="font-medium">{selectedDate}</span>
          </div>
        </div>
      </div>

      {/* Performance Metrics */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
          Performance Overview
        </h2>
        <PlantPerformancePanel performance={performance} loading={loading} />
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Alerts Dashboard - Takes 2 columns */}
        <div className="lg:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
            Alert Management
          </h2>
          <AlertDashboard
            alertsData={alertsData}
            loading={loading}
            onAcknowledge={handleAcknowledgeAlert}
            onResolve={handleResolveAlert}
            onFilterChange={handleAlertFilterChange}
          />
        </div>

        {/* Communication Status - Takes 1 column */}
        <div>
          <CommunicationStatusCard
            communicationData={communicationData}
            loading={loading}
          />
        </div>
      </div>

      {/* Real-time Status Indicator */}
      <div className="fixed bottom-4 right-4">
        <div className="bg-white dark:bg-gray-800 p-3 rounded-lg border shadow-lg">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
            <span className="text-sm text-gray-600 dark:text-gray-400">Live Monitoring</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GroundMountedDashboard;